import React from 'react';
import { connect } from 'react-redux';
import { Modal, Text, Theme } from '@simplybenefits/sb-ui-library';
import { useLottie } from "lottie-react";
import checkMarkConfetti from "../../../../assets/check-mark-confetti.json";
import pendingClaim from "../../../../assets/pending-claim-animation.json";

/**
 * This is the check mark animation component which uses the lottie-react library
 */
const CheckMarkAnimation = () => {
    const options = {
        animationData: checkMarkConfetti,
        loop: true,
        style: { width: '75%', margin: '0 auto' }
    };

    const { View } = useLottie(options);

    return (
        <div 
            style={{
                marginTop: -60,
                marginBottom: -50
            }}
        >
            { View }
        </div>
    );
};

/**
 * This is the check mark animation component which uses the lottie-react library
 */
const PendingClaimAnimation = () => {
    const options = {
        animationData: pendingClaim,
        loop: true,
        style: { width: '60%', margin: '0 auto' }
    };

    const { View } = useLottie(options);

    return (
        <div
            style={{
                marginTop: -30,
                marginBottom: -40
            }}
        >
            { View }
        </div>
    );
};

/**
 * @param type
 * @param onClose
 * @param eob
 * @param reasonsForFlagging
 * @param id
 * @param loader
 * @param verbiage
 * @param isProbe
 * @param isPending
 * @param onSubmitReal
 * @param onReviewClaim
 * @returns {JSX.Element}
 * @constructor
 */
const ClaimSuccessModal = ({ type, onClose, eob, reasonsForFlagging = [], id, loader, verbiage, isProbe, isPending, onSubmitReal, onReviewClaim }) => (
    <Modal
        open={true}
        loader={ loader }
        onClose={ onClose }
        primaryButton={{ label: 'Done', onClick: onClose }}
        secondaryButton={
            isProbe && onSubmitReal
                ? { label: 'Submit Transaction', onClick: onSubmitReal }
                : isPending && onReviewClaim
                    ? { label: 'Review Claim', onClick: onReviewClaim }
                    : (
                        !verbiage ?
                            {
                                label: "Download EOB",
                                onClick: () => {
                                    const linkSource = `data:application/pdf;base64,${ eob }`;
                                    const downloadLink = document.createElement("a");
                                    let fileName = `Simply_Benefits_EOB_${ id }.pdf`;

                                    if (type.toUpperCase() === 'PREDETERMINATION') {
                                        fileName = `Simply_Predetermination_${ id }.pdf`;
                                    }

                                    downloadLink.href = linkSource;
                                    downloadLink.download = fileName;
                                    downloadLink.click();
                                }
                            }
                            : null
                    )
        }
    >
        {
            isPending ?
                <PendingClaimAnimation />
                :
                <CheckMarkAnimation />
        }

        <Text
            align="center"
            display="block"
            customStyle={{ fontSize: 32 }}
        >
            { isProbe ? `Dental Probe Completed!` : `Dental Transaction ${ isPending ? 'Pending' : 'Completed' }!` }
        </Text>

        <Text
            align="center"
            display="block"
            margin="20px 0 0 0"
            medium
        >
            {
                verbiage ||
                (
                    isProbe ?
                        `This was a probe submission. No transaction was actually submitted. Please refer to the summary data below to preview what the transaction results would look like.`
                        :
                        `Dental transaction was successfully sent through the dental system. Please refer to the EOB data below to determine the status of the transaction.`
                )
            }
        </Text>

        {
            Boolean(reasonsForFlagging && reasonsForFlagging.length > 0) &&
                <Text
                    small
                    bold
                    margin="10px 0"
                >
                    Reason for flagging:
                </Text>
        }

        {
            Boolean(reasonsForFlagging) &&
                reasonsForFlagging.map((reason) => (
                    <Text
                        small
                        margin="10px 0 0 0"
                    >
                        { reason }
                    </Text>
                ))
        }

        {
            isProbe && eob &&
                <div style={{ textAlign: 'center', marginTop: 20 }}>
                    <button
                        onClick={ () => {
                            const linkSource = `data:application/pdf;base64,${ eob }`;
                            const downloadLink = document.createElement("a");
                            downloadLink.href = linkSource;
                            downloadLink.download = `Simply_Benefits_Probe_${ id }.pdf`;
                            downloadLink.click();
                        }}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            background: 'none',
                            border: `1px solid ${ Theme.colors.lightGrey }`,
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: 13,
                            color: Theme.colors.mainTextGrey,
                            padding: '7px 14px',
                            transition: 'border-color 0.15s ease, color 0.15s ease'
                        }}
                        onMouseEnter={ (e) => {
                            e.currentTarget.style.borderColor = Theme.colors.skyBlue;
                            e.currentTarget.style.color = Theme.colors.skyBlue;
                        }}
                        onMouseLeave={ (e) => {
                            e.currentTarget.style.borderColor = Theme.colors.lightGrey;
                            e.currentTarget.style.color = Theme.colors.mainTextGrey;
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                            <line x1="16" y1="13" x2="8" y2="13" />
                            <line x1="16" y1="17" x2="8" y2="17" />
                            <polyline points="10 9 9 9 8 9" />
                        </svg>
                        View Summary
                    </button>
                </div>
        }
    </Modal>
);

export default connect()(ClaimSuccessModal);